import React, { Component } from 'react';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import '../Pagination.css';
import '../common/MdPage.css';
import * as constantClass from '../fileWithConstants';
import { createPagedGridController } from '../../hooks/pagedGridClassAdapter';
import { displayDate } from '../../services/dates/dateService';
import * as mesasService from '../../services/mesas/mesasService';
import { classifyMesasError, formatMesaPeriodoConLlamado, formatMesaPeriodoDescripcion } from '../../services/mesas/mesasDto';
import { ConfirmDialog } from '../common/ConfirmDialog';
import {
  buildStandardRowActions,
  MdRowActions,
  MdTableActionsCell,
  MdTableCell,
} from '../common/MdRowActions';
import { PlusIcon, SearchIcon, UploadIcon } from '../common/mdPageIcons';

function canShowGenerarActa(mesa) {
  return (
    new Date(mesa.mesaExamenFechaFinInscripcion).setHours(0, 0, 0, 0)
    < new Date().setHours(0, 0, 0, 0)
    && mesa.generarActa
  );
}

export class MDMesaExamen extends Component {
  static displayName = MDMesaExamen.name;

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      searchInput: '',
      perPage: 10,
      paginationData: [],
      pageCount: 0,
      forcePage: 0,
      showErrorMessage: false,
      errorMessage: '',
      successMessage: '',
      deletingId: null,
      pendingDelete: null,
      pendingGenerarActa: null,
      generandoActaId: null,
    };
    this.grid = createPagedGridController({
      fetchPage: (query) => mesasService.listMesas(query),
      defaultPageSize: 10,
      onChange: (gridState) => this.applyGridState(gridState),
    });
    this.searchInputRef = React.createRef();
  }

  applyGridState(gridState) {
    this.setState({
      paginationData: gridState.items,
      pageCount: gridState.pageCount,
      searchInput: gridState.search,
      perPage: gridState.pageSize,
      loading: gridState.loading,
      forcePage: gridState.forcePage,
    });
  }

  async componentDidMount() {
    const canAccess = await constantClass.canAccess('WWMesaExamen');
    if (canAccess) {
      this.populateData();
    }
  }

  handlePageClick = (e) => {
    this.grid.handlePageClick(e).catch((error) => this.handleMesasError(error));
  };

  handleSearchInputChange = (e) => {
    const searchInput = e.target.value;
    const cleared = searchInput.trim() === '';
    this.setState({ searchInput, successMessage: '', showErrorMessage: false, errorMessage: '' });
    if (cleared && this.grid.getState().search.trim() !== '') {
      this.grid.clearSearchIfNeeded(this.grid.getState().search).catch((error) => this.handleMesasError(error));
    }
  };

  handleSearchSubmit = (e) => {
    e.preventDefault();
    const search = (this.searchInputRef.current
      ? this.searchInputRef.current.value
      : this.state.searchInput).trim();
    this.setState({ searchInput: search });
    this.grid.submitSearch(search).catch((error) => this.handleMesasError(error));
  };

  handleRemoveItem = (mesa) => {
    if (this.state.deletingId) {
      return;
    }
    const id = mesa.mesaExamenCod;
    this.setState({
      pendingDelete: {
        id,
        nombre: (mesa.materiaCarreraNombre || '').trim(),
        carrera: (mesa.carreraNombre || '').trim(),
        periodo: formatMesaPeriodoConLlamado(mesa),
      },
    });
  };

  handleCancelDelete = () => {
    if (this.state.deletingId) {
      return;
    }
    this.setState({ pendingDelete: null });
  };

  handleConfirmDelete = () => {
    const { pendingDelete } = this.state;
    if (!pendingDelete || this.state.deletingId) {
      return;
    }
    this.deleteItem(pendingDelete.id);
  };

  handleGenerarActaRequest = (mesa) => {
    if (this.state.generandoActaId) {
      return;
    }
    this.setState({
      pendingGenerarActa: {
        id: mesa.mesaExamenCod,
        nombre: (mesa.materiaCarreraNombre || '').trim(),
        carrera: (mesa.carreraNombre || '').trim(),
      },
    });
  };

  handleCancelGenerarActa = () => {
    if (this.state.generandoActaId) {
      return;
    }
    this.setState({ pendingGenerarActa: null });
  };

  handleConfirmGenerarActa = () => {
    const { pendingGenerarActa } = this.state;
    if (!pendingGenerarActa || this.state.generandoActaId) {
      return;
    }
    this.generarActa(pendingGenerarActa.id);
  };

  buildRowActions(mesa) {
    const id = String(mesa.mesaExamenCod).trim();
    const extraActions = [];

    if (canShowGenerarActa(mesa)) {
      extraActions.push({
        key: 'generar-acta',
        label: 'Generar acta',
        variant: 'outline',
        icon: 'clipboard',
        onClick: () => this.handleGenerarActaRequest(mesa),
        disabled: Boolean(this.state.generandoActaId),
      });
    }

    return buildStandardRowActions({
      editTo: '/update-mex/' + id + '/0',
      onDelete: () => this.handleRemoveItem(mesa),
      deleteDisabled: this.state.loading || this.state.deletingId === id,
      deleteLabel: this.state.deletingId === id ? 'Eliminando...' : 'Borrar',
      detailTo: '/update-mex/' + id + '/1',
      extraActions,
    });
  }

  renderTable(items) {
    return (
      <div className="md-page__table-card md-mex__table-card">
        <div className="md-mex__table-scroll">
          <table className="md-page__table md-page__table--actions-fixed md-page__table--mex-grid" aria-labelledby="md-mex-title">
            <thead>
              <tr>
                <th scope="col" className="md-page__table__col-materia">Materia</th>
                <th scope="col" className="md-page__table__col-carrera">Carrera</th>
                <th scope="col" className="md-page__table__col-periodo-examen">Período de examen</th>
                <th scope="col" className="md-page__table__col-fecha-examen">Fecha de examen</th>
                <th scope="col" className="md-page__table__col-fecha-examen">Inicio inscripción</th>
                <th scope="col" className="md-page__table__col-fecha-examen">Fin inscripción</th>
                <th scope="col" className="md-page__table__actions-header" aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {items.map((mesa) => (
                <tr key={mesa.mesaExamenCod}>
                  <MdTableCell className="md-page__table__col-materia">{mesa.materiaCarreraNombre}</MdTableCell>
                  <MdTableCell className="md-page__table__col-carrera">{mesa.carreraNombre}</MdTableCell>
                  <MdTableCell className="md-page__table__col-periodo-examen">
                    {formatMesaPeriodoDescripcion(mesa)}
                  </MdTableCell>
                  <MdTableCell className="md-page__table__col-fecha-examen">
                    {displayDate(mesa.mesaExamenFechaExamen)}
                  </MdTableCell>
                  <MdTableCell className="md-page__table__col-fecha-examen">
                    {displayDate(mesa.mesaExamenFechaInicioInscripci)}
                  </MdTableCell>
                  <MdTableCell className="md-page__table__col-fecha-examen">
                    {displayDate(mesa.mesaExamenFechaFinInscripcion)}
                  </MdTableCell>
                  <MdTableActionsCell>
                    <MdRowActions items={this.buildRowActions(mesa)} />
                  </MdTableActionsCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    const isBusy = this.state.loading;
    const listLoading = this.state.loading && !this.state.deletingId && !this.state.generandoActaId;
    const { pendingDelete, pendingGenerarActa } = this.state;

    const contents = listLoading
      ? <p className="md-page__loading"><em>Cargando datos...</em></p>
      : this.renderTable(this.state.paginationData);

    return (
      <div className="md-page md-mex">
        <div className="md-page__header">
          <h1 id="md-mex-title" className="md-page__title">Manejar Datos de Mesa de Examenes</h1>
        </div>

        {!this.state.showErrorMessage ? (
          <div className="md-page__toolbar">
            <Link className="md-page__action-link" to="/add-mex">
              <PlusIcon />
              Nueva Mesa de Examen
            </Link>
            <Link className="md-page__action-link" to="/cme">
              <UploadIcon />
              Cargar desde archivo
            </Link>
          </div>
        ) : null}

        <div className="md-page__feedback" aria-live="polite" role="status">
          {this.state.showErrorMessage ? (
            <div className="md-page__error" role="alert">{this.state.errorMessage}</div>
          ) : null}
          {this.state.successMessage ? (
            <div className="md-page__success">{this.state.successMessage}</div>
          ) : null}
        </div>

        {!this.state.showErrorMessage ? (
          <>
            <form className="md-page__search-row" onSubmit={this.handleSearchSubmit}>
              <div className="md-page__search-wrap">
                <span className="md-page__search-icon">
                  <SearchIcon />
                </span>
                <input
                  ref={this.searchInputRef}
                  className="md-page__search-input"
                  type="text"
                  name="search"
                  autoComplete="off"
                  disabled={isBusy}
                  placeholder="Ingrese el período de examen o la materia..."
                  value={this.state.searchInput}
                  onChange={this.handleSearchInputChange}
                />
              </div>
              <button type="submit" className="md-page__search-btn" disabled={isBusy}>
                Buscar
              </button>
            </form>

            {contents}

            {!listLoading && this.state.pageCount > 1 ? (
              <div className="md-page__pagination">
                <ReactPaginate
                  previousLabel="Anterior"
                  nextLabel="Siguiente"
                  breakLabel="..."
                  breakClassName="break-me"
                  pageCount={this.state.pageCount}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={5}
                  onPageChange={this.handlePageClick}
                  forcePage={this.state.forcePage}
                  containerClassName="pagination"
                  subContainerClassName="pages pagination"
                  activeClassName="active"
                />
              </div>
            ) : null}
          </>
        ) : null}

        <ConfirmDialog
          open={Boolean(pendingDelete)}
          title="Eliminar mesa de examen"
          message="Esta acción eliminará la mesa de examen y no se puede deshacer."
          detail={
            pendingDelete
              ? `${pendingDelete.nombre} - ${pendingDelete.carrera} (${pendingDelete.periodo})`
              : ''
          }
          confirmLabel="Eliminar"
          cancelLabel="Cancelar"
          variant="danger"
          loading={Boolean(this.state.deletingId)}
          onConfirm={this.handleConfirmDelete}
          onCancel={this.handleCancelDelete}
        />

        <ConfirmDialog
          open={Boolean(pendingGenerarActa)}
          title="Generar acta de examen"
          message="¿Desea generar la/s Acta/s de Examen?"
          detail={
            pendingGenerarActa
              ? `${pendingGenerarActa.nombre} - ${pendingGenerarActa.carrera}`
              : ''
          }
          confirmLabel="Generar"
          cancelLabel="Cancelar"
          variant="primary"
          loading={Boolean(this.state.generandoActaId)}
          onConfirm={this.handleConfirmGenerarActa}
          onCancel={this.handleCancelGenerarActa}
        />
      </div>
    );
  }

  handleMesasError(error) {
    const outcome = classifyMesasError(error);
    if (outcome.type === 'timeout') {
      constantClass.RedirectToLogin();
      return;
    }
    this.setState({
      showErrorMessage: true,
      errorMessage: outcome.message,
      loading: false,
    });
  }

  async populateData() {
    try {
      await this.grid.load();
    } catch (error) {
      this.handleMesasError(error);
    }
  }

  async deleteItem(id) {
    this.setState({ deletingId: id, showErrorMessage: false, errorMessage: '' });
    try {
      await mesasService.deleteMesa(id);
      this.setState({ successMessage: 'El registro se eliminó correctamente.', pendingDelete: null });
      await this.grid.reloadAfterDelete(this.state.paginationData.length);
    } catch (error) {
      this.handleMesasError(error);
    } finally {
      this.setState({ deletingId: null });
    }
  }

  async generarActa(id) {
    this.setState({ generandoActaId: id, showErrorMessage: false, errorMessage: '' });
    try {
      await mesasService.generarActaExamen(id);
      this.setState({
        successMessage: 'Actas generadas exitosamente. Si las desea imprimir, vaya al menú de Actas de Examenes.',
        pendingGenerarActa: null,
      });
      await this.grid.reloadAfterDelete();
    } catch (error) {
      this.handleMesasError(error);
    } finally {
      this.setState({ generandoActaId: null });
    }
  }
}
